import { createFetcher } from '@finn/ui-utils';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import useSWR from 'swr';

import { Deal, useDeal } from '../../../hooks/useDeal';
import { useHandoverMetadata } from '../../../hooks/useHandoverMetadata';
import { ChangeFee, UpdateHandoverData } from '../../../types/bff-api';

export const constructHandoverRequest = (
  formData: Record<string, string>,
  deal?: Deal | null,
  { firstAvailableHandoverDate }: { firstAvailableHandoverDate?: string } = {}
): UpdateHandoverData | null => {
  const nonEmptyFields = [
    'handover_firstname',
    'handover_lastname',
    'handover_housenumber',
    'handover_street',
    'handover_city',
    'handover_zip',
    'handover_phone_number',
    'preferred_handover_date',
  ];

  const fallbackDate = firstAvailableHandoverDate
    ? dayjs(firstAvailableHandoverDate).set('hour', 11).toDate()
    : (undefined as unknown as Date);

  const candidateData = {
    handover_firstname: formData?.firstname || deal?.handover_firstname || '',
    handover_lastname: formData?.lastname || deal?.handover_lastname || '',
    handover_housenumber:
      formData?.housenumber || deal?.handover_housenumber || '',
    handover_street: formData?.street || deal?.handover_street || '',
    handover_city: formData?.city || deal?.handover_city || '',
    handover_zip: formData?.postcode || deal?.handover_zip || '',
    handover_phone_number:
      formData?.phone_number || deal?.handover_phone_number || '',
    handover_address_extra:
      formData?.address_suffix || deal?.handover_address_extra || undefined,
    preferred_handover_date:
      formData?.slot?.includes('{') || !formData?.slot
        ? fallbackDate
        : (formData?.slot as unknown as Date),
  };

  // if some mandatory fields are empty, return null
  if (
    Object.entries(candidateData).some(
      ([key, value]) => nonEmptyFields?.includes(key) && !value
    )
  ) {
    return null;
  }

  return candidateData;
};

const generateKey = (handoverData: UpdateHandoverData) => {
  return `${handoverData.handover_street}-${handoverData.handover_housenumber}-${handoverData.handover_city}-${handoverData.handover_zip}-${handoverData.preferred_handover_date?.toISOString?.()}`;
};

export const useHandoverChangeFee = (dealId?: string | null) => {
  // this hook always is as part of form
  const formMethods = useFormContext();
  const { data: deal } = useDeal(dealId || '');
  const { data: handoverMetadata, status } = useHandoverMetadata(dealId || '');
  const availabilities = handoverMetadata?.availabilities || [];
  const firstAvailableHandoverDate = availabilities?.[0]?.date;
  const handoverData = constructHandoverRequest(
    formMethods?.getValues?.(),
    deal,
    { firstAvailableHandoverDate }
  );
  const isAnyInputFocused =
    typeof window === 'object' && document.activeElement?.tagName === 'INPUT';
  const shouldFetch =
    dealId &&
    Boolean(deal) &&
    !!handoverData &&
    !formMethods?.getValues('return_type') &&
    !isAnyInputFocused &&
    firstAvailableHandoverDate;

  const { data: responseData, isLoading } = useSWR<{
    data: ChangeFee;
  }>(
    shouldFetch
      ? `${dealId}/handover-change-fee:${generateKey(handoverData)}`
      : null,
    shouldFetch
      ? () =>
          createFetcher({
            baseURL: '',
            withCredentials: true,
          })({
            method: 'POST',
            url: `/portal/api/bff/deals/${dealId}/handover-change-fee`,
            body: handoverData,
          }) as Promise<{
            data: ChangeFee;
          }>
      : null
  );

  return {
    data: responseData?.data?.amount,
    isLoading: isLoading || status === 'loading',
  };
};
